import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Avatar,
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Edit as EditIcon, 
  Add as AddIcon, 
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  YouTube as YouTubeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

const BlogAdmin = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openVideoDialog, setOpenVideoDialog] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [postToDelete, setPostToDelete] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'For Hosts',
    author: 'FreshSweeper Team',
    image: '',
    seoTitle: '',
    seoDescription: '',
    tags: '',
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [videoUrl, setVideoUrl] = useState('');

  const extractYouTubeThumbnail = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?#]+)/,
      /youtube\.com\/shorts\/([^&?#]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
      }
    }
    return null;
  };

  const showError = (msg) => {
    setErrorMessage(msg);
    setOpenErrorDialog(true);
  };

  const openVideoDialogFn = () => {
    setVideoUrl('');
    setOpenVideoDialog(true);
  };

  const processVideoUrl = () => {
    if (!videoUrl) {
      showError('Please enter a YouTube URL');
      return;
    }
    
    const videoId = videoUrl.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/embed\/)([^&?#]+)/)?.[1];
    if (!videoId) {
      showError('Invalid YouTube URL');
      return;
    }
    
    const embedUrl = `https://www.youtube.com/embed/${videoId}`;
    const thumbnailUrl = extractYouTubeThumbnail(videoUrl);
    
    if (thumbnailUrl) {
      setFormData({ 
        ...formData, 
        image: thumbnailUrl,
        content: formData.content + `\n\n<div class="video-responsive"><iframe width="100%" height="400" src="${embedUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>\n\n`
      });
      setMessage({ type: 'success', text: '✅ Video added with thumbnail!' });
    } else {
      setFormData({ 
        ...formData, 
        content: formData.content + `\n\n<div class="video-responsive"><iframe width="100%" height="400" src="${embedUrl}" frameborder="0" allowfullscreen></iframe></div>\n\n`
      });
      setMessage({ type: 'success', text: '✅ Video added!' });
    }
    
    setOpenVideoDialog(false);
    setVideoUrl('');
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  useEffect(() => {
    const saved = localStorage.getItem('freshsweeper_blog_posts');
    if (saved) {
      setPosts(JSON.parse(saved));
    } else {
      const defaults = [{
        id: 1,
        title: "How to Automate Your Airbnb Turnovers",
        slug: "automate-turnovers",
        excerpt: "Learn how to set up automated scheduling...",
        date: "2024-04-28",
        category: "For Hosts",
        author: "FreshSweeper Team",
        image: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=600&h=400&fit=crop",
        content: "<p>Automating your turnovers is the single best thing you can do...</p>",
        seoTitle: "",
        seoDescription: "",
        tags: "Airbnb, cleaning, automation"
      }];
      setPosts(defaults);
      localStorage.setItem('freshsweeper_blog_posts', JSON.stringify(defaults));
    }
  }, []);

  const savePosts = (updated) => {
    localStorage.setItem('freshsweeper_blog_posts', JSON.stringify(updated));
    setPosts(updated);
  };

  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleSubmit = () => {
    if (!formData.title || !formData.content) {
      setMessage({ type: 'error', text: 'Title and content are required!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    const slug = generateSlug(formData.title);
    const date = new Date().toISOString().split('T')[0];
    
    const newPost = {
      id: editingPost ? editingPost.id : Date.now(),
      title: formData.title,
      slug: slug,
      excerpt: formData.excerpt || formData.content.substring(0, 150).replace(/#/g, '').trim() + '...',
      date: date,
      category: formData.category,
      author: formData.author,
      image: formData.image || 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&h=400&fit=crop',
      content: formData.content,
      seoTitle: formData.seoTitle,
      seoDescription: formData.seoDescription,
      tags: formData.tags,
    };

    if (editingPost) {
      savePosts(posts.map(p => p.id === editingPost.id ? newPost : p));
      setMessage({ type: 'success', text: 'Post updated!' });
    } else {
      savePosts([newPost, ...posts]);
      setMessage({ type: 'success', text: 'Post created!' });
    }
    resetForm();
    setOpenDialog(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      author: post.author,
      image: post.image || '',
      seoTitle: post.seoTitle || '',
      seoDescription: post.seoDescription || '',
      tags: post.tags || '',
    });
    setOpenDialog(true);
  };

  const handleDeleteClick = (id) => {
    setPostToDelete(id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (postToDelete) {
      savePosts(posts.filter(p => p.id !== postToDelete));
      setMessage({ type: 'success', text: 'Post deleted!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    }
    setOpenDeleteDialog(false);
    setPostToDelete(null);
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setPostToDelete(null);
  };

  const resetForm = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: 'For Hosts',
      author: 'FreshSweeper Team',
      image: '',
      seoTitle: '',
      seoDescription: '',
      tags: '',
    });
    setTabValue(0);
  };

  const handleViewPost = (slug) => navigate(`/blog/${slug}`);

  const extractThumbnail = () => {
    const match = formData.content.match(/youtube\.com\/embed\/([^"?]+)/);
    if (match && match[1]) {
      setFormData({ ...formData, image: `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg` });
      setMessage({ type: 'success', text: '✅ Thumbnail set!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } else {
      setMessage({ type: 'info', text: 'No YouTube video found.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight="800">
        📝 Blog Admin
      </Typography>
      
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setOpenDialog(true); }}>
              Create New Post
            </Button>
            <Button variant="outlined" startIcon={<YouTubeIcon />} onClick={openVideoDialogFn}>
              Add YouTube Video
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => {
              const saved = localStorage.getItem('freshsweeper_blog_posts');
              if (saved) setPosts(JSON.parse(saved));
            }}>
              Refresh
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              All Posts ({posts.length})
            </Typography>
            
            {posts.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No posts yet. Click "Create New Post" to get started!
              </Typography>
            ) : (
              <List>
                {posts.map((post) => (
                  <ListItem key={post.id} divider>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      {post.image && <Avatar src={post.image} variant="rounded" sx={{ width: 56, height: 56 }} />}
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" fontWeight="600">{post.title}</Typography>
                            <Chip label={post.category} size="small" />
                            <Chip label={post.date} size="small" variant="outlined" />
                          </Box>
                        }
                        secondary={post.excerpt?.substring(0, 100) + '...'}
                      />
                    </Box>
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleViewPost(post.slug)}><ViewIcon /></IconButton>
                      <IconButton edge="end" onClick={() => handleEdit(post)}><EditIcon /></IconButton>
                      <IconButton edge="end" onClick={() => handleDeleteClick(post.id)}><DeleteIcon /></IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPost ? 'Edit Post' : 'Create New Post'}</DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label="Content" /><Tab label="SEO" /><Tab label="Preview" />
          </Tabs>
          
          {tabValue === 0 && (
            <Box>
              <TextField fullWidth label="Title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} margin="normal" required />
              <TextField fullWidth select label="Category" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} margin="normal" SelectProps={{ native: true }}>
                <option value="For Hosts">For Hosts</option><option value="For Cleaners">For Cleaners</option><option value="General">General</option>
              </TextField>
              <TextField fullWidth label="Author" value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} margin="normal" />
              <TextField fullWidth label="Featured Image URL" value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} margin="normal" helperText="Paste an image URL or add YouTube video" />
              <Button size="small" variant="outlined" onClick={extractThumbnail} sx={{ mt: 1, mb: 2 }}>Extract Thumbnail from YouTube</Button>
              <TextField fullWidth label="Excerpt" value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} margin="normal" multiline rows={2} />
              <TextField fullWidth label="Content" value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} margin="normal" multiline rows={12} required placeholder="Write here. Supports Markdown and HTML." />
            </Box>
          )}
          
          {tabValue === 1 && (
            <Box>
              <TextField fullWidth label="SEO Title" value={formData.seoTitle} onChange={(e) => setFormData({ ...formData, seoTitle: e.target.value })} margin="normal" />
              <TextField fullWidth label="SEO Description" value={formData.seoDescription} onChange={(e) => setFormData({ ...formData, seoDescription: e.target.value })} margin="normal" multiline rows={3} />
              <TextField fullWidth label="Tags" value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} margin="normal" helperText="Comma separated" />
            </Box>
          )}
          
          {tabValue === 2 && (
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, maxHeight: 500, overflow: 'auto' }}>
              {formData.image && <img src={formData.image} alt="Preview" style={{ width: '100%', borderRadius: 8, marginBottom: 16 }} />}
              <ReactMarkdown>{formData.content || '*Preview will appear here*'}</ReactMarkdown>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{editingPost ? 'Update' : 'Publish'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent><Typography>Are you sure you want to delete this post? This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Video URL Dialog */}
      <Dialog open={openVideoDialog} onClose={() => setOpenVideoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add YouTube Video</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="YouTube URL" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} margin="normal" placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..." helperText="The thumbnail will be used as featured image." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVideoDialog(false)}>Cancel</Button>
          <Button onClick={processVideoUrl} variant="contained">Add Video</Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={openErrorDialog} onClose={() => setOpenErrorDialog(false)}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent><Typography>{errorMessage}</Typography></DialogContent>
        <DialogActions><Button onClick={() => setOpenErrorDialog(false)}>OK</Button></DialogActions>
      </Dialog>
    </Container>
  );
};

export default BlogAdmin;
